import { Injectable, NgZone, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import {
  SESSION_BROADCAST_CHANNEL,
  SESSION_IDLE_SECONDS,
  SESSION_LAST_ACTIVITY_KEY,
  SESSION_TOTAL_MS,
  SESSION_WARNING_MS,
  SESSION_WARNING_SECONDS,
} from 'src/app/core/constants/session.constants';
import { isSessionBroadcastMessage } from 'src/app/core/utils/broadcast-message.util';
import { AuthLogoutService } from 'src/app/services/auth/auth-logout.service';
import { InactivityStateService } from './inactivity-state.service';
import { TokenRefreshService } from './token-refresh.service';

interface SessionBroadcastMessage {
  type: 'activity' | 'logout';
  ts?: number;
}

@Injectable({ providedIn: 'root' })
export class InactivityService {
  private readonly idle = inject(Idle);
  private readonly auth = inject(AuthService);
  private readonly authLogout = inject(AuthLogoutService);
  private readonly inactivityState = inject(InactivityStateService);
  private readonly tokenRefresh = inject(TokenRefreshService);
  private readonly ngZone = inject(NgZone);
  private readonly channel = new BroadcastChannel(SESSION_BROADCAST_CHANNEL);

  private isWatching = false;
  private sessionInitialized = false;
  private policyMonitorId: ReturnType<typeof setInterval> | null = null;
  private logoutTriggered = false;

  constructor() {
    this.configureIdle();
    this.bindIdleEvents();
    this.bindBroadcastChannel();
    this.bindAuthentication();
  }

  /** Restores session timers after page reload for authenticated users. */
  initializeSession(recoveryMode: boolean): void {
    if (this.sessionInitialized) {
      return;
    }

    this.sessionInitialized = true;
    this.logoutTriggered = false;

    if (recoveryMode) {
      this.recoverFromSessionStorage();
      return;
    }

    this.resetTimer();
    this.startWatching();
  }

  onApiActivity(): void {
    if (!this.isWatching) {
      return;
    }

    this.resetTimer();
  }

  resetTimer(): void {
    this.touchActivity();
    this.applyDefaultTimers();
    this.idle.interrupt();
    this.channel.postMessage({
      type: 'activity',
      ts: Date.now(),
    } satisfies SessionBroadcastMessage);
    this.setStateInZone('active');
  }

  /** Full session extension: resets inactivity timers and refreshes the Auth0 access token. */
  continueSession(): void {
    this.resumeInterrupts();
    this.resetTimer();
    void this.tokenRefresh.refreshSessionToken();
  }

  logoutFromWarning(): void {
    this.resumeInterrupts();
    this.stopWatching();
    this.authLogout.logout();
  }

  resetTimerWithRemaining(remainingMs: number): void {
    const remainingSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    this.touchActivity();
    this.channel.postMessage({
      type: 'activity',
      ts: Date.now(),
    } satisfies SessionBroadcastMessage);
    this.logoutTriggered = false;

    if (remainingSeconds > SESSION_WARNING_SECONDS) {
      this.idle.setIdle(remainingSeconds - SESSION_WARNING_SECONDS);
      this.idle.setTimeout(SESSION_WARNING_SECONDS);
      this.setStateInZone('active');
      this.idle.interrupt();
      return;
    }

    this.startWarning(remainingSeconds);
  }

  startWarning(remainingSeconds: number): void {
    this.setStateInZone('warning');
    this.pauseInterrupts();
    // ng-idle requires idle > 0; use 1s so timeout phase drives the warning window.
    this.idle.setIdle(1);
    this.idle.setTimeout(Math.max(1, remainingSeconds));

    if (!this.isWatching) {
      this.startWatching();
      return;
    }

    this.idle.interrupt();
  }

  stopWatching(): void {
    if (!this.isWatching) {
      return;
    }

    this.stopPolicyMonitor();
    this.idle.stop();
    this.isWatching = false;
    this.sessionInitialized = false;
    this.setStateInZone('active');
  }

  logoutDueToInactivity(): void {
    if (this.logoutTriggered) {
      return;
    }

    this.logoutTriggered = true;
    this.setStateInZone('timedout');
    this.stopWatching();
    this.authLogout.logout({ reason: 'inactivity' });
  }

  getRemainingLogoutMs(): number {
    const raw = sessionStorage.getItem(SESSION_LAST_ACTIVITY_KEY);
    const lastActivity = raw ? Number(raw) : Date.now();
    return Math.max(0, SESSION_TOTAL_MS - (Date.now() - lastActivity));
  }

  private configureIdle(): void {
    this.idle.setIdle(SESSION_IDLE_SECONDS);
    this.idle.setTimeout(SESSION_WARNING_SECONDS);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
  }

  private bindIdleEvents(): void {
    this.idle.onIdleStart.subscribe(() => {
      this.setStateInZone('warning');
      this.pauseInterrupts();
    });

    this.idle.onIdleEnd.subscribe(() => {
      if (this.getRemainingLogoutMs() > SESSION_WARNING_MS) {
        this.resumeInterrupts();
        this.setStateInZone('active');
      }
    });

    this.idle.onTimeout.subscribe(() => {
      this.logoutDueToInactivity();
    });

    this.idle.onInterrupt.subscribe(() => {
      if (!this.isWatching || this.inactivityState.getValue() === 'warning') {
        return;
      }

      this.touchActivity();
      if (this.getRemainingLogoutMs() > SESSION_WARNING_MS) {
        this.setStateInZone('active');
      }
    });
  }

  private bindBroadcastChannel(): void {
    this.channel.onmessage = (event: MessageEvent<SessionBroadcastMessage>) => {
      if (!isSessionBroadcastMessage(event.data)) {
        return;
      }

      const message = event.data;

      if (message.type === 'activity') {
        if (message.ts) {
          sessionStorage.setItem(SESSION_LAST_ACTIVITY_KEY, message.ts.toString());
        }
        this.logoutTriggered = false;
        this.idle.interrupt();
        this.setStateInZone('active');
        return;
      }

      if (message.type === 'logout') {
        this.stopWatching();
      }
    };
  }

  private bindAuthentication(): void {
    this.auth.isAuthenticated$
      .pipe(
        filter((isAuthenticated) => isAuthenticated !== undefined),
        distinctUntilChanged(),
      )
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          return;
        }

        this.stopWatching();
        sessionStorage.removeItem(SESSION_LAST_ACTIVITY_KEY);
      });
  }

  private recoverFromSessionStorage(): void {
    const raw = sessionStorage.getItem(SESSION_LAST_ACTIVITY_KEY);
    const lastActivity = raw ? Number(raw) : Date.now();

    if (!raw) {
      sessionStorage.setItem(SESSION_LAST_ACTIVITY_KEY, lastActivity.toString());
      this.resetTimer();
      this.startWatching();
      return;
    }

    const elapsed = Date.now() - lastActivity;

    if (elapsed >= SESSION_TOTAL_MS) {
      this.logoutDueToInactivity();
      return;
    }

    if (elapsed >= SESSION_IDLE_SECONDS * 1000) {
      const warningRemaining = Math.ceil((SESSION_TOTAL_MS - elapsed) / 1000);
      this.startWarning(warningRemaining);
      return;
    }

    const idleRemaining = Math.ceil((SESSION_IDLE_SECONDS * 1000 - elapsed) / 1000);
    this.idle.setIdle(idleRemaining);
    this.idle.setTimeout(SESSION_WARNING_SECONDS);
    this.setStateInZone('active');
    this.startWatching();
  }

  private applyDefaultTimers(): void {
    this.idle.setIdle(SESSION_IDLE_SECONDS);
    this.idle.setTimeout(SESSION_WARNING_SECONDS);
  }

  private startWatching(): void {
    if (this.isWatching) {
      return;
    }

    this.idle.watch();
    this.isWatching = true;
    this.startPolicyMonitor();
  }

  private startPolicyMonitor(): void {
    this.stopPolicyMonitor();
    this.policyMonitorId = setInterval(() => {
      this.ngZone.run(() => {
        this.evaluateSessionPolicy();
      });
    }, 1000);
  }

  private stopPolicyMonitor(): void {
    if (this.policyMonitorId !== null) {
      clearInterval(this.policyMonitorId);
      this.policyMonitorId = null;
    }
  }

  /** Authoritative warning/logout timing based on lastActivity (survives ng-idle edge cases). */
  private evaluateSessionPolicy(): void {
    if (!this.isWatching || this.logoutTriggered) {
      return;
    }

    const remainingMs = this.getRemainingLogoutMs();

    if (remainingMs <= 0) {
      this.logoutDueToInactivity();
      return;
    }

    if (remainingMs <= SESSION_WARNING_MS) {
      this.setStateInZone('warning');
      return;
    }

    this.setStateInZone('active');
  }

  private touchActivity(): void {
    sessionStorage.setItem(SESSION_LAST_ACTIVITY_KEY, Date.now().toString());
    this.logoutTriggered = false;
  }

  private pauseInterrupts(): void {
    for (const interrupt of this.idle.getInterrupts()) {
      interrupt.pause();
    }
  }

  private resumeInterrupts(): void {
    for (const interrupt of this.idle.getInterrupts()) {
      interrupt.resume();
    }
  }

  private setStateInZone(state: Parameters<InactivityStateService['setState']>[0]): void {
    this.ngZone.run(() => {
      this.inactivityState.setState(state);
    });
  }
}
